import { useEffect, useMemo, useRef, type RefObject } from 'react';
import type { SbStage, StoryboardHistory, StoryboardInputBody } from '@agentic-ivy/shared-types';
import { storyboardApi } from '@/services/storyboardApi';
import { useTheme } from '@/hooks/useTheme';
import { studioBHostStyle } from '@/lib/studioB/hostStyle';
import { studioBOn } from '@/lib/studioB/flag';
import { ivyStateOf } from '@/lib/ivyChat/ivyNow';
import { createStoryboardHost } from './storyboardHost';

interface Options {
  conversationId: string;
  frameRef: RefObject<HTMLIFrameElement | null>;
  viewUrl: string | null;
  version: number | null;
  onPendingChange: (pending: number) => void;
  onSent: (inputId: string, body: StoryboardInputBody) => void;
  onNavigate: (version: number) => void;
  /** The page said it is ready: its stage and the history of every page, for the app's page menu. */
  onPage?: (page: { stage: SbStage | null; history: StoryboardHistory | null }) => void;
  /** F4: the open version is one Ivy just made from a storyboard send (the page shows a toast). */
  fresh?: boolean;
  onSeeLooks?: () => void;
}

const originOf = (url: string | null) => {
  if (!url) return null;
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
};

export function useStoryboardBridge({ conversationId, frameRef, viewUrl, version, onPendingChange, onSent, onNavigate, onPage, onSeeLooks, fresh = false }: Options) {
  const { theme, isDarkMode, setTheme } = useTheme();
  const latest = useRef({ viewUrl, version, onPendingChange, onSent, onNavigate, onPage, onSeeLooks, theme, isDarkMode, setTheme, fresh });
  latest.current = { viewUrl, version, onPendingChange, onSent, onNavigate, onPage, onSeeLooks, theme, isDarkMode, setTheme, fresh };

  const host = useMemo(
    () =>
      createStoryboardHost({
        conversationId,
        api: storyboardApi,
        frame: () => frameRef.current?.contentWindow ?? null,
        frameOrigin: () => originOf(latest.current.viewUrl),
        version: () => latest.current.version,
        onPendingChange: (n) => latest.current.onPendingChange(n),
        onSent: (inputId, body) => latest.current.onSent(inputId, body),
        theme: () => ({ mode: latest.current.theme, dark: latest.current.isDarkMode }),
        onSetTheme: (mode) => latest.current.setTheme(mode),
        onNavigate: (v) => latest.current.onNavigate(v),
        onSeeLooks: () => latest.current.onSeeLooks?.(),
        onReady: (_fields, page) => latest.current.onPage?.(page),
        ownsChrome: true,
        hostStyle: () => studioBHostStyle(),
        plainGround: studioBOn(),
        ivy: () => {
          if (!studioBOn()) return null;
          const v = latest.current.version;
          return { state: ivyStateOf(conversationId), ...(v != null ? { version: v } : {}), ...(latest.current.fresh ? { fresh: true } : {}) };
        },
      }),
    [conversationId, frameRef],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent) => void host.handle(event);
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [host]);

  useEffect(() => {
    host.theme({ mode: theme, dark: isDarkMode });
  }, [host, theme, isDarkMode]);

  useEffect(() => {
    const onIvy = (e: Event) => {
      const { conversationId: id, state } = (e as CustomEvent).detail ?? {};
      const v = latest.current.version;
      if (id === conversationId && studioBOn()) host.ivy({ state, ...(v != null ? { version: v } : {}) });
    };
    window.addEventListener('ivy-state', onIvy);
    return () => window.removeEventListener('ivy-state', onIvy);
  }, [host, conversationId]);

  useEffect(() => {
    const onSeek = (e: Event) => {
      const { conversationId: id, t } = (e as CustomEvent).detail ?? {};
      if (id === conversationId && typeof t === 'number') host.seek(t);
    };
    window.addEventListener('storyboard-seek', onSeek);
    return () => window.removeEventListener('storyboard-seek', onSeek);
  }, [host, conversationId]);

  return host;
}
